import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getProfile } from "../lib/api";
import { startSubscriptionCheckout } from "../lib/publicCheckout";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  LayoutDashboard,
  Activity,
  Users,
  FileText,
  Settings,
  CreditCard,
  CalendarDays,
  Menu,
  X,
  LogOut,
  User,
  UserRound,
  Plus,
  Shield,
  ClipboardPlus,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Logo, BRAND_NAME, BRAND_SUBTITLE } from "./ui/Logo";
import { isUsClinicalHost } from "../lib/usClinicalHost";
import DemoBanner from "./DemoBanner";
import DemoFloatingCTA from "./DemoFloatingCTA";

function sidebarItemIsActive(pathname, itemPath) {
  if (itemPath === "/configuracion/billing") {
    return pathname === "/configuracion/billing" || pathname.startsWith("/configuracion/billing/");
  }
  if (itemPath === "/configuracion") {
    return pathname === "/configuracion";
  }
  if (itemPath === "/nueva-consulta") {
    return pathname === "/nueva-consulta" || pathname.startsWith("/nueva-consulta/");
  }
  if (itemPath === "/pacientes") {
    return pathname === "/pacientes" || pathname.startsWith("/pacientes/");
  }
  if (itemPath === "/dashboard") {
    return pathname === "/dashboard" || pathname.startsWith("/dashboard/") || pathname === "/panel/visual-v1";
  }
  if (itemPath === "/vgi-rapida" || itemPath === "/reportes") {
    return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
  }
  return pathname === itemPath;
}

const LATAM_SIDEBAR_EXPANDED_KEY = "aurora_latam_sidebar_expanded";

// Custom icon component for elderly healthcare
const ElderlyHealthIcon = ({ size = "normal" }) => {
  const sizeClasses = size === "small" ? "h-4 w-4" : "h-5 w-5";
  const badgeSize = size === "small" ? "h-[40%] w-[40%]" : "h-[45%] w-[45%]";
  return (
    <div className={`relative ${sizeClasses}`}>
      <UserRound className="h-full w-full text-white" strokeWidth={1.5} />
      <div className={`absolute -bottom-0.5 -right-0.5 ${badgeSize} bg-white rounded-full flex items-center justify-center`}>
        <Plus className="h-[70%] w-[70%] text-sky-600" strokeWidth={3} />
      </div>
    </div>
  );
};

// Aurora Geriátrica — Doctor-centered navigation.
// "Evaluaciones" removed: scales are now integrated inside the consultation
// and patient profile workflow (Step 5 of NewConsultation + PatientProfile tabs).
const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Panel" },
  { path: "/agenda", icon: CalendarDays, label: "Agenda" },
  { path: "/pacientes", icon: Users, label: "Pacientes" },
  { path: "/reportes", icon: FileText, label: "Historial" },
  { path: "/configuracion/billing", icon: CreditCard, label: "Suscripción" },
  { path: "/configuracion", icon: Settings, label: "Configuración" },
];

export const Layout = ({ children, hideSidebar = false }) => {
  const isUsHost = isUsClinicalHost();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [latamSidebarExpanded, setLatamSidebarExpanded] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(LATAM_SIDEBAR_EXPANDED_KEY) : null;
      return raw !== "false";
    } catch {
      return true;
    }
  });
  const [doctorName, setDoctorName] = useState("");
  const { user, logout, daysRemaining, subscriptionStatus, isBillingExempt, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getProfile();
        if (response.data.nombre_completo) {
          setDoctorName(response.data.nombre_completo);
        } else {
          setDoctorName(user?.nombre || (isUsHost ? "User" : "Usuario"));
        }
      } catch {
        setDoctorName(user?.nombre || (isUsHost ? "User" : "Usuario"));
      }
    };
    fetchProfile();
  }, [user, isUsHost]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const showBillingCta = !isBillingExempt && subscriptionStatus !== "active";
  const resolvedNavItems = isUsHost
    ? [
        { path: "/dashboard", icon: LayoutDashboard, label: "Clinical Overview" },
        { path: "/agenda", icon: CalendarDays, label: "Schedule" },
        { path: "/pacientes", icon: Users, label: "Patients" },
        { path: "/nueva-consulta", icon: ClipboardPlus, label: "New Visit" },
        { path: "/reportes", icon: FileText, label: "Visit History" },
        { path: "/vgi-rapida", icon: Activity, label: "Quick CGA" },
        { path: "/configuracion/billing", icon: CreditCard, label: "Subscription & Billing" },
        { path: "/configuracion", icon: Settings, label: "Practice Settings" },
      ]
    : navItems;

  const handlePlanActivation = () => {
    startSubscriptionCheckout({
      userId: user?.id || user?.user_id || user?.uuid || "",
      email: user?.email || "",
      name: user?.nombre || doctorName || "",
    });
  };

  const toggleLatamSidebar = () => {
    setLatamSidebarExpanded((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(LATAM_SIDEBAR_EXPANDED_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Mobile Header */}
      {!hideSidebar && (
      <header className="fixed left-0 right-0 top-0 z-40 flex h-16 items-center justify-between border-b border-slate-200/90 bg-white/95 px-3 backdrop-blur-sm supports-[backdrop-filter]:bg-white/80 lg:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
          className="h-10 w-10 shrink-0"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <Link
          to="/dashboard"
          className="flex min-w-0 flex-1 items-center justify-center px-2"
          aria-label={BRAND_NAME}
        >
          <Logo
            variant="wordmark"
            format="png"
            className="h-[1.65rem] w-auto max-h-8 max-w-[min(52vw,220px)] object-contain object-center sm:h-7"
            alt=""
          />
        </Link>
        <div className="h-10 w-10 shrink-0" aria-hidden />
      </header>
      )}

      {/* Sidebar Overlay */}
      {!hideSidebar && sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar: USA (desktop + móvil) | LATAM (drawer móvil + desktop colapsable) */}
      {!hideSidebar && (
      <aside
        data-aurora-tour="dashboard-nav"
        className={`fixed left-0 top-0 z-40 h-full border-r border-slate-100 bg-white/80 backdrop-blur-sm transition-[transform,width] duration-300 ease-in-out
          w-64
          ${isUsHost
            ? `${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"} lg:w-20 xl:w-64`
            : `${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"} ${latamSidebarExpanded ? "lg:w-64" : "lg:w-20"}`
          }`}
      >
        <div
          className={`flex h-16 shrink-0 items-center border-b border-slate-100 bg-white/70 px-2 py-2 ${
            isUsHost ? "justify-center px-3" : "justify-between gap-1 px-2 lg:px-3"
          }`}
        >
          {isUsHost ? (
            <Logo variant="icon" size="sm" className="h-9 w-9 opacity-[0.97]" alt={BRAND_NAME} />
          ) : (
            <>
              <Link
                to="/dashboard"
                onClick={() => setSidebarOpen(false)}
                className="flex min-w-0 flex-1 items-center justify-center overflow-hidden lg:justify-start"
                aria-label={BRAND_NAME}
              >
                <Logo
                  variant="icon"
                  format="svg"
                  size="sm"
                  className="h-9 w-9 shrink-0 opacity-[0.97] lg:hidden"
                  alt={BRAND_NAME}
                />
                {latamSidebarExpanded ? (
                  <Logo
                    variant="wordmark"
                    format="png"
                    className="hidden h-7 w-auto max-w-[min(148px,42vw)] object-contain object-left opacity-[0.98] lg:block"
                    alt=""
                  />
                ) : (
                  <Logo
                    variant="icon"
                    format="svg"
                    size="sm"
                    className="mx-auto hidden h-9 w-9 shrink-0 opacity-[0.97] lg:block"
                    alt={BRAND_NAME}
                  />
                )}
              </Link>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="hidden h-9 w-9 shrink-0 text-slate-500 hover:bg-slate-50 lg:flex"
                onClick={toggleLatamSidebar}
                aria-label={latamSidebarExpanded ? "Contraer menú lateral" : "Expandir menú lateral"}
              >
                {latamSidebarExpanded ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              </Button>
            </>
          )}
        </div>

        <ScrollArea className="h-[calc(100vh-10rem)]">
          <nav className="space-y-3 p-3">
            {resolvedNavItems.map((item) => {
              const isActive = sidebarItemIsActive(location.pathname, item.path);
              const linkRow = isUsHost
                ? "flex items-center gap-3 rounded-2xl px-3 py-3 text-[15px] transition-all duration-200 lg:justify-center xl:justify-start"
                : `flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] transition-colors duration-200 lg:px-2 ${latamSidebarExpanded ? "lg:justify-start" : "lg:justify-center"}`;
              const activeRow = isUsHost
                ? isActive
                  ? "bg-indigo-50 font-medium text-indigo-600"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-600"
                : isActive
                  ? "bg-sky-50 font-medium text-sky-700"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-600";
              const iconTone = isUsHost
                ? isActive
                  ? "text-indigo-600"
                  : "text-slate-500"
                : isActive
                  ? "text-sky-700"
                  : "text-slate-500";
              const vgiChipClasses =
                isUsHost
                  ? "ml-auto inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide lg:hidden xl:inline-flex"
                  : `ml-auto rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide inline-flex ${latamSidebarExpanded ? "lg:inline-flex" : "lg:hidden"}`;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={item.label}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.replace(/\//g, "-").replace(/^-/, "") || "dashboard"}`}
                  className={`${linkRow} ${activeRow}`}
                >
                  <item.icon className={`h-5 w-5 shrink-0 ${iconTone}`} />
                  <span
                    className={`font-medium tracking-tight ${isUsHost ? "lg:hidden xl:inline" : latamSidebarExpanded ? "lg:inline" : "lg:hidden"}`}
                  >
                    {item.label}
                  </span>
                  {item.path === "/vgi-rapida" && (
                    <span
                      className={`${vgiChipClasses} ${
                        isActive
                          ? isUsHost
                            ? "border-indigo-200/80 bg-white/80 text-indigo-700"
                            : "border-sky-100 bg-sky-50 text-sky-700"
                          : "border-slate-200 bg-slate-50 text-slate-500"
                      }`}
                    >
                      {isUsHost ? "Quick" : "Rápido"}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-100 bg-white/70 p-3 xl:p-4">
          {isAdmin && (
            <Link
              to="/admin"
              title={isUsHost ? "Practice Admin" : "Panel Admin"}
              onClick={() => setSidebarOpen(false)}
              className={`mb-2 flex items-center gap-3 rounded-2xl px-3 py-2 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-600 ${
                isUsHost ? "lg:justify-center xl:justify-start" : latamSidebarExpanded ? "lg:justify-start" : "lg:justify-center"
              }`}
            >
              <Shield className={`h-4 w-4 shrink-0 ${isUsHost ? "text-indigo-600" : "text-sky-700"}`} />
              <span className={`text-sm font-medium ${isUsHost ? "lg:hidden xl:inline" : latamSidebarExpanded ? "lg:inline" : "lg:hidden"}`}>
                {isUsHost ? "Practice Admin" : "Panel Admin"}
              </span>
            </Link>
          )}
          <div
            className={`mb-3 flex items-center gap-3 ${
              isUsHost ? "lg:justify-center xl:justify-start" : latamSidebarExpanded ? "lg:justify-start" : "lg:justify-center"
            }`}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-200/90">
              <User className="h-5 w-5 text-slate-600" />
            </div>
            <div
              className={`min-w-0 flex-1 ${isUsHost ? "lg:hidden xl:block" : latamSidebarExpanded ? "lg:block" : "lg:hidden"}`}
            >
              <p className="truncate text-sm font-medium text-slate-800">Dr. {doctorName}</p>
              <p className="truncate text-xs text-slate-500">{user?.email}</p>
              <p className="mt-0.5 truncate text-[10px] text-slate-400">
                {isBillingExempt
                  ? (isUsHost ? "Exempt" : "Exento")
                  : subscriptionStatus === "active"
                    ? (isUsHost ? "Active" : "Activo")
                    : daysRemaining != null
                      ? (isUsHost ? `Trial: ${daysRemaining}d` : `Prueba: ${daysRemaining}d`)
                      : subscriptionStatus || ""}
              </p>
            </div>
          </div>
          {showBillingCta && (
            <Button
              className={`mb-3 w-full bg-sky-600 text-white hover:bg-sky-700 ${isUsHost ? "lg:px-2 xl:px-4" : latamSidebarExpanded ? "lg:px-4" : "lg:px-2"}`}
              onClick={handlePlanActivation}
              data-testid="sidebar-buy-now-button"
              title={!isUsHost && !latamSidebarExpanded ? "Activar plan" : undefined}
            >
              {isUsHost ? (
                <>
                  <span className="lg:hidden xl:inline">Upgrade to Pro</span>
                  <span className="hidden lg:inline xl:hidden">Pro</span>
                </>
              ) : (
                <>
                  <span className={latamSidebarExpanded ? "inline" : "inline lg:hidden"}>Activar plan</span>
                  <span className={latamSidebarExpanded ? "hidden" : "hidden lg:inline"}>Pro</span>
                </>
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            title={isUsHost ? "Sign Out" : "Cerrar Sesión"}
            className={`w-full justify-start text-slate-500 hover:bg-slate-50 hover:text-slate-600 ${
              isUsHost ? "lg:justify-center xl:justify-start" : latamSidebarExpanded ? "lg:justify-start" : "lg:justify-center lg:px-2"
            }`}
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="mr-2 h-4 w-4 shrink-0" />
            <span className={`${isUsHost ? "lg:hidden xl:inline" : latamSidebarExpanded ? "lg:inline" : "lg:hidden"}`}>
              {isUsHost ? "Sign Out" : "Cerrar Sesión"}
            </span>
          </Button>
        </div>
      </aside>
      )}

      {/* Main Content */}
      <main
        className={`flex min-h-screen min-w-0 flex-col overflow-x-hidden ${
          hideSidebar
            ? "pt-0 lg:ml-0"
            : `pt-16 ${isUsHost ? "lg:ml-20 xl:ml-64 lg:pt-0" : `lg:pt-0 ${latamSidebarExpanded ? "lg:ml-64" : "lg:ml-20"}`}`
        }`}
      >
        {user?.email === "demo@aurorageriatrica.com" && <DemoBanner />}
        {showBillingCta && (
          <div className="border-b border-amber-100 bg-amber-50/95 px-4 py-3 lg:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0">
            <p className="text-sm text-amber-950 pr-2">
              {isUsHost
                ? "Your trial is still active. Subscribe now to avoid interruptions."
                : "Tu prueba sigue activa. Suscríbete para continuar sin interrupciones."}
            </p>
            <Button
              size="sm"
              className="shrink-0 bg-sky-600 hover:bg-sky-700 text-white w-full sm:w-auto"
              onClick={handlePlanActivation}
              data-testid="topbar-buy-now-button"
            >
              {isUsHost ? "Upgrade to Pro" : "Comprar ahora"}
            </Button>
          </div>
        )}
        <div
          className={`min-w-0 flex-1 p-4 lg:p-8 ${!isUsHost ? "mx-auto w-full max-w-[1400px]" : "max-w-full"}`}
        >
          {children}
        </div>
        
        {/* Footer - minimal branding */}
        <footer className="lg:ml-0 py-3 px-8 text-center text-slate-400 text-xs border-t border-slate-100 bg-white">
          <p className="text-slate-500">© 2026 Aurora Geriátrica, S.A. · Guatemala</p>
          <p className="text-slate-400 mt-1.5">
            Desarrollado en Guatemala por profesionales de salud y tecnología.
          </p>
          <nav className="mt-1.5 flex justify-center gap-4 text-[10px] text-slate-400">
            <Link to="/terminos" className="hover:text-slate-600">
              Términos
            </Link>
            <span>·</span>
            <Link to="/privacidad" className="hover:text-slate-600">
              Privacidad
            </Link>
            <span>·</span>
            <Link to="/datos" className="hover:text-slate-600">
              Datos clínicos
            </Link>
          </nav>
        </footer>
        <DemoFloatingCTA />
      </main>
    </div>
  );
};

export default Layout;
